import React, { useMemo, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PlusIcon, ArrowUpIcon, ArrowDownIcon, ArrowUpDownIcon } from 'lucide-react';
import { Condition, ConditionStatus, STATUS_COLORS, SortField, SortDirection } from '../types/condition';
import { ConditionCard } from './ConditionCard';
type StatusColumnProps = {
  status: ConditionStatus;
  conditions: Condition[];
  onAddClick: (status: ConditionStatus) => void;
  onUpdateAssignee?: (id: string, assignee: string) => void;
};
type ColumnSortField = 'title' | 'dueDate' | 'assignee';
export function StatusColumn({
  status,
  conditions,
  onAddClick,
  onUpdateAssignee
}: StatusColumnProps) {
  const [columnSortField, setColumnSortField] = useState<ColumnSortField>('dueDate');
  const [columnSortDirection, setColumnSortDirection] = useState<SortDirection>('asc');
  const {
    setNodeRef,
    isOver
  } = useDroppable({
    id: status
  });
  const colors = STATUS_COLORS[status];
  // Sort conditions within this column
  const sortedConditions = useMemo(() => {
    return [...conditions].sort((a, b) => {
      let comparison = 0;
      switch (columnSortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'dueDate':
          comparison = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          break;
        case 'assignee':
          comparison = a.assignee.localeCompare(b.assignee);
          break;
      }
      return columnSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [conditions, columnSortField, columnSortDirection]);
  const conditionIds = sortedConditions.map(c => c.id);
  const handleSort = (field: ColumnSortField) => {
    if (columnSortField === field) {
      setColumnSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setColumnSortField(field);
      setColumnSortDirection('asc');
    }
  };
  const SortIcon = ({
    field
  }: {
    field: ColumnSortField;
  }) => {
    if (columnSortField !== field) {
      return <ArrowUpDownIcon className="w-3 h-3 text-gray-400" data-id="element-314" />;
    }
    return columnSortDirection === 'asc' ? <ArrowUpIcon className="w-3 h-3 text-blue-600" data-id="element-315" /> : <ArrowDownIcon className="w-3 h-3 text-blue-600" data-id="element-316" />;
  };
  return <div className="flex flex-col min-w-[280px] max-w-[320px] flex-1" data-id="element-317">
      {/* Column Header */}
      <div className={`${colors.headerBg} rounded-t-xl px-4 py-3 border-b border-gray-200`} data-id="element-318">
        <div className="flex items-center justify-between mb-2" data-id="element-319">
          <div className="flex items-center gap-2" data-id="element-320">
            <h2 className={`font-semibold ${colors.text}`} data-id="element-321">{status}</h2>
            <span className={`
                inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 
                text-xs font-medium rounded-full bg-white/60 ${colors.text}
              `} data-id="element-322">
              {conditions.length}
            </span>
          </div>
          <button onClick={() => onAddClick(status)} className={`
              p-1 rounded-md hover:bg-white/50 transition-colors
              ${colors.text} focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
            `} aria-label={`Add condition to ${status}`} data-id="element-323">
            <PlusIcon className="w-4 h-4" data-id="element-324" />
          </button>
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-1" data-id="element-325">
          <span className="text-xs text-gray-500 mr-1" data-id="element-326">Sort:</span>
          <button onClick={() => handleSort('title')} className={`
              px-2 py-0.5 text-xs rounded flex items-center gap-1 transition-colors
              ${columnSortField === 'title' ? 'bg-white/80 text-gray-900 font-medium' : 'text-gray-600 hover:bg-white/50'}
            `} aria-label="Sort by title" data-id="element-327">
            Title
            <SortIcon field="title" data-id="element-328" />
          </button>
          <button onClick={() => handleSort('dueDate')} className={`
              px-2 py-0.5 text-xs rounded flex items-center gap-1 transition-colors
              ${columnSortField === 'dueDate' ? 'bg-white/80 text-gray-900 font-medium' : 'text-gray-600 hover:bg-white/50'}
            `} aria-label="Sort by due date" data-id="element-329">
            Due
            <SortIcon field="dueDate" data-id="element-330" />
          </button>
          <button onClick={() => handleSort('assignee')} className={`
              px-2 py-0.5 text-xs rounded flex items-center gap-1 transition-colors
              ${columnSortField === 'assignee' ? 'bg-white/80 text-gray-900 font-medium' : 'text-gray-600 hover:bg-white/50'}
            `} aria-label="Sort by assignee" data-id="element-331">
            Assignee
            <SortIcon field="assignee" data-id="element-332" />
          </button>
        </div>
      </div>

      {/* Column Body */}
      <div ref={setNodeRef} className={`
          flex-1 ${colors.bg} rounded-b-xl p-2 overflow-y-auto
          transition-colors duration-200 min-h-[200px]
          ${isOver ? 'ring-2 ring-blue-400 ring-inset bg-blue-50' : ''}
        `} data-id="element-333">
        <SortableContext items={conditionIds} strategy={verticalListSortingStrategy} data-id="element-334">
          <div className="space-y-2" data-id="element-335">
            {sortedConditions.map(condition => <ConditionCard key={condition.id} condition={condition} onUpdateAssignee={onUpdateAssignee} data-id="element-336" />)}
          </div>
        </SortableContext>

        {/* Empty State */}
        {conditions.length === 0 && <div className="flex flex-col items-center justify-center h-32 text-gray-400" data-id="element-337">
            <p className="text-sm" data-id="element-338">No conditions</p>
            <button onClick={() => onAddClick(status)} className="mt-2 text-xs text-blue-500 hover:text-blue-600 hover:underline focus:outline-none" data-id="element-339">
              Add one
            </button>
          </div>}
      </div>
    </div>;
}